<script setup lang="ts">
    import { getContent } from '@views/Web/Forms.access';
    import type { FormListItem, WebContent } from '@views/Web/Forms.types';
    import { ref, onMounted, watch } from 'vue';
    import InputRadio from '@shared/components/InputRadio.vue';
    import type { widgetRuleset } from '@views/Web/WidgetService.types';
    import { randomString } from '@cwd/lib/randomString';

    let props = defineProps<{
        form: FormListItem | null;
        widgetRuleset: widgetRuleset | null;
        currentFormsRules: string;
    }>();

    let selected = ref('none');
    let webForm = ref<WebContent>();
    let currentRule = ref(props.widgetRuleset?.formsRules().find((rule: any) => rule.ownedByObjectId == props.form?.WebContentId));

    currentRule.value && (selected.value = currentRule.value.id.substring(0, currentRule.value.id.indexOf('-')));

    onMounted(async () => {
        let forms = await getContent(props.form?.WebContentId as string);
        webForm.value = forms[0];
    });

    const defaultThrottle = { maxRepetitions: '1', maxPerSession: undefined, minTimeout: undefined };

    const options: { [key: string]: any } = {
        delay5: {
            id: 'delay5',
            throttle: defaultThrottle,
            filters: [],
            triggers: [{ type: 'delay', params: { timeToAction: 5 } }],
            action: {
                type: 'pop',
                params: {},
            },
        },
        delay20: {
            id: 'delay20',
            filters: [],
            throttle: defaultThrottle,
            triggers: [{ type: 'delay', params: { timeToAction: 20 } }],
            action: {
                type: 'pop',
                params: {},
            },
        },
        scroll45: {
            id: 'scroll45',
            filters: [],
            throttle: defaultThrottle,
            triggers: [{ type: 'scroll', params: { percent: 45 } }],
            action: {
                type: 'pop',
                params: {},
            },
        },
    };

    const updateRule = (newValue: string) => {
        if (!props.widgetRuleset) return;

        let currentHtml;

        if (currentRule.value && props.form?.WebContentId) {
            currentHtml = currentRule.value.action.params.content;
            props.widgetRuleset.remove(props.form.WebContentId);
        }

        if (newValue !== 'none') {
            let rule = props.widgetRuleset.newRule(newValue, 'signup-form');
            let templateRule = options[newValue];
            rule.id += '-' + randomString(5);
            rule.filters = templateRule.filters;
            rule.throttle = templateRule.throttle;
            rule.triggers = templateRule.triggers;
            rule.action = templateRule.action;
            currentHtml && (rule.action.params.content = currentHtml);

            props.form && (rule.ownedByObjectId = props.form.WebContentId);
            props.widgetRuleset.addRule(rule);
            currentRule.value = rule;

            if (!currentRule.value.action.params.content) updateHtml();
        }

        rulesComparison();
    };

    const updateHtml = () => {
        if (!currentRule.value || !webForm.value) return;
        currentRule.value.action.params.content = webForm.value.Data.renderedHtml;

        rulesComparison();
    };

    const availableRules = [
        { value: 'none', name: $Resources.RuleEditorRuleDontShow },
        { value: 'delay5', name: $Resources.RuleEditorRuleShowAfter5Seconds },
        { value: 'delay20', name: $Resources.RuleEditorRuleShowAfter20Seconds },
        { value: 'scroll45', name: $Resources.RuleEditorRuleShowAfter45PercentScroll },
    ];

    const emit = defineEmits(['update:rules']);

    const rulesComparison = () => {
        let hasChanges = props.currentFormsRules !== props.widgetRuleset?.serialize();
        emit('update:rules', hasChanges);
    };

    const hasHtmlChanges = () => {
        if (!currentRule.value || !webForm.value) return false;
        return currentRule.value.action.params.content !== webForm.value.Data.renderedHtml;
    };
</script>
<template>
    <div class="m-sm-b">
        <button type="button" class="btn btn-default" v-show="hasHtmlChanges()" @click="updateHtml">
            {{ $Resources.RuleEditorUpdateHtml }}
        </button>
    </div>

    <div>
        <InputRadio
            v-for="rule in availableRules"
            :checked="selected === rule.value"
            :value="rule.value"
            name="availableRules"
            @update:checked="(newValue) => updateRule(newValue)">
            <template #label>{{ rule.name }}</template>
        </InputRadio>
    </div>
</template>

<style scoped></style>
